import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SectionHeader from '@/components/shared/SectionHeader';
import { publicApi } from '@/lib/api';
import {
    ArrowRight,
    Images as ImagesIcon,
    Video as VideoIcon,
    Camera,
} from 'lucide-react';

/**
 * Home Gallery section — shows the 4 latest gallery albums.
 * Falls back to legacy flat gallery items if albums are empty.
 */
const GallerySection = ({ items = [] }) => {
    const [albums, setAlbums] = useState([]);

    useEffect(() => {
        publicApi.getGalleryAlbums().then(setAlbums).catch(() => {});
    }, []);

    const latestAlbums = albums.slice(0, 4);

    if (latestAlbums.length === 0 && items.length === 0) return null;

    return (
        <section className="section-pad" data-testid="home-gallery-section">
            <div className="container-wide">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <SectionHeader
                        eyebrow="Gallery"
                        title="Moments from the field."
                        description="Camps, career sessions, tree drives and community meets — captured on the ground."
                    />
                    <Link
                        to="/gallery"
                        className="text-sm font-semibold text-[hsl(var(--primary))] flex items-center gap-1 hover:underline"
                        data-testid="gallery-view-all"
                    >
                        View full gallery <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>

                {latestAlbums.length > 0 ? (
                    <div
                        className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
                        data-testid="home-gallery-albums-grid"
                    >
                        {latestAlbums.map((a, i) => {
                            const photoCount = (a.photos || []).length;
                            const videoCount = (a.videos || []).length;
                            return (
                                <motion.div
                                    key={a.id}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.06 }}
                                    viewport={{ once: true, margin: '-40px' }}
                                >
                                    <Link
                                        to="/gallery"
                                        className="block h-full"
                                        data-testid={`home-gallery-album-${a.id}`}
                                    >
                                        <Card className="h-full bg-card border-border card-lift overflow-hidden">
                                            <div className="aspect-[4/3] bg-muted overflow-hidden relative">
                                                {a.cover_image ? (
                                                    <img
                                                        src={a.cover_image}
                                                        alt={a.title}
                                                        loading="lazy"
                                                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary))]/10 to-[hsl(var(--accent))]/10">
                                                        <Camera className="h-8 w-8 text-muted-foreground" />
                                                    </div>
                                                )}
                                                <div className="absolute top-2 right-2 flex gap-1.5">
                                                    {photoCount > 0 && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-white/90 text-[hsl(var(--primary))] backdrop-blur-sm font-semibold text-[10px] h-5"
                                                        >
                                                            <ImagesIcon className="h-2.5 w-2.5 mr-0.5" />
                                                            {photoCount}
                                                        </Badge>
                                                    )}
                                                    {videoCount > 0 && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-[hsl(var(--accent))]/90 text-[hsl(var(--accent-foreground))] backdrop-blur-sm font-semibold text-[10px] h-5"
                                                        >
                                                            <VideoIcon className="h-2.5 w-2.5 mr-0.5" />
                                                            {videoCount}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {a.category && (
                                                    <div className="absolute bottom-2 left-2">
                                                        <Badge
                                                            variant="outline"
                                                            className="bg-white/90 backdrop-blur-sm text-[10px] border-white/60"
                                                        >
                                                            {a.category}
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                            <CardContent className="p-4">
                                                <div className="font-display font-semibold text-base leading-snug line-clamp-2">
                                                    {a.title}
                                                </div>
                                                {a.caption && (
                                                    <div className="mt-1 text-xs text-muted-foreground leading-relaxed line-clamp-2">
                                                        {a.caption}
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    // Legacy fallback — flat photo grid
                    <div
                        className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
                        data-testid="gallery-grid"
                    >
                        {items.slice(0, 8).map((g, i) => (
                            <Link
                                to="/gallery"
                                key={g.id || i}
                                className="relative group overflow-hidden rounded-xl bg-muted"
                            >
                                <div className="aspect-square">
                                    <img
                                        src={g.thumbnail_url || g.url}
                                        alt={g.title || 'Gallery'}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default GallerySection;
